import Cookie from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { AWSRegions } from "../generated/ups/graphql";

type TokenPayload = {
  "cognito:username": string;
  email: string;
  region: AWSRegions;
};

export const getDomain = (domainName: string) => {
  return domainName.split(".").slice(-3).join(".");
};

const expiredAtAfter = (ms: number) => new Date(new Date().getTime() + ms);

const httpOnly = false;
const ONE_HOUR_IN_MS = 60 * 60 * 1000;
const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
const longPeriodExpire = 30;
const shotTokensLifetime = ONE_HOUR_IN_MS;
const longTokensLifetime = ONE_HOUR_IN_MS * 12;

export const MAXIMUM_LOGIN_ATTEMPTS = 5;

const commonConfig = {
  secure: true,
  httpOnly,
  sameSite: "strict" as const,
};

const setCookie = (
  name: string,
  value: string,
  domain: string,
  options: Cookie.CookieAttributes | undefined,
) => {
  Cookie.set(name, value, {
    ...commonConfig,
    domain,
    ...options,
  });
};

export const clearLoginAttempts = (domainName: string) => {
  const domain = getDomain(domainName);
  Cookie.remove("loginAttempts", { domain });
};

export const increaseLoginAttempts = (domainName: string) => {
  const domain = getDomain(domainName);
  const loginAttempts = parseInt(Cookie.get("loginAttempts") || "", 10) || 0;
  const currentLoginAttempts = loginAttempts + 1;

  Cookie.set("loginAttempts", currentLoginAttempts.toString(), {
    ...commonConfig,
    expires: expiredAtAfter(ONE_HOUR_IN_MS),
    domain,
  });

  return currentLoginAttempts;
};

export const setSessionId = (sessionId: string, domainName: string) => {
  const rootDomain = getDomain(domainName);
  setCookie("sessionId", sessionId, rootDomain, {
    ...commonConfig,
    expires: expiredAtAfter(FIVE_MINUTES_IN_MS), // COGNITO_USER_POOL_AUTHENTICATION_SESSION_DURATION parameter in backend
  });
};

export const setUserLookupsCookies = (
  user: {
    username: string;
    email: string;
    region: string;
    authMethod: string;
    redirectTo: string;
  },
  domainName: string,
) => {
  const { username, email, region, authMethod } = user;
  const domain = getDomain(domainName);

  Cookie.set("username", username, {
    domain,
    expires: longPeriodExpire,
  });

  Cookie.set("email", email, {
    domain,
    expires: longPeriodExpire,
  });

  Cookie.set("region", region, {
    domain,
    expires: longPeriodExpire,
  });

  Cookie.set("authMethod", authMethod, {
    domain,
    expires: longPeriodExpire,
  });

  Cookie.set("redirectTo", user.redirectTo, {
    domain,
    expires: shotTokensLifetime,
  });
};

export const getUserLookupData = () => {
  const email = Cookie.get("email");
  const username = Cookie.get("username");
  const region = Cookie.get("region");
  const authMethod = Cookie.get("authMethod");
  const redirectTo = Cookie.get("redirectTo");

  return {
    email,
    username,
    region,
    authMethod,
    redirectTo,
  };
};

export const setLoginCookies = (
  tokens: { idToken: string; accessToken: string; refreshToken: string },
  domainName: string,
) => {
  const { idToken, accessToken, refreshToken } = tokens;
  const domain = getDomain(domainName);

  Cookie.set("accessToken", accessToken, {
    domain,
    expires: expiredAtAfter(shotTokensLifetime),
  });

  if (refreshToken) {
    Cookie.set("refreshToken", refreshToken, {
      domain,
      expires: expiredAtAfter(longTokensLifetime),
    });
  }

  Cookie.set("idToken", idToken, {
    domain,
    expires: expiredAtAfter(shotTokensLifetime),
  });
};

export const getAccessToken = () => {
  const accessToken = Cookie.get("accessToken");
  if (!accessToken) {
    return null;
  }
  return accessToken;
};

export const getRefreshToken = () => {
  const refreshToken = Cookie.get("refreshToken");

  if (!refreshToken) {
    return null;
  }

  return refreshToken;
};

export const getIdToken = () => {
  const idToken = Cookie.get("idToken");

  if (!idToken) {
    return null;
  }

  return idToken;
};

export const deleteLoginCookies = (domainName: string) => {
  const domain = getDomain(domainName);

  Cookie.remove("accessToken", { domain });
  Cookie.remove("refreshToken", { domain });
  Cookie.remove("idToken", { domain });
};

export const deleteLookupsUserCookies = (domainName: string) => {
  const domain = getDomain(domainName);

  Cookie.remove("username", { domain });
  Cookie.remove("email", { domain });
  Cookie.remove("region", { domain });
  Cookie.remove("authMethod", { domain });
  Cookie.remove("redirectTo", { domain });
};

export const deleteSessionId = (domainName: string) => {
  const rootDomain = getDomain(domainName);
  Cookie.remove("sessionId", { domain: rootDomain });
};

export const getCurrentUser = () => {
  const idToken = Cookie.get("idToken");
  if (!idToken) {
    return null;
  }
  const decoded = jwtDecode<TokenPayload>(idToken);
  return {
    username: decoded["cognito:username"],
    email: decoded.email,
    region: decoded.region,
  };
};
